// CourseDraft — what a user fills out in the course-creation form.
//
// Lifecycle:
//   form (in-progress) → CourseDraft (Zod-valid, in IndexedDB)
//   submit to ingest → ingest returns { manifest: CourseManifest, data: IngestPackage }
//
// Decision history:
//   Session 19A: created. type: CourseTypeSchema (single enum), template: TemplateId.
//   Session 19D: replaced type with discipline + pedagogyTypes (multi). Templates
//                removed; defaults derive from (discipline × pedagogyTypes) via
//                `defaultComponentsFor`. Custom escape hatches via customDiscipline
//                and customPedagogy free-text fields.
import { z } from 'zod';
import { ComponentIdSchema } from './component-id.js';
import { ContentLanguageSchema } from './content-language.js';
import { DisciplineSchema } from './discipline.js';
import { PedagogyTypeSchema } from './pedagogy-type.js';
import { MasteryLevelSchema } from './mastery-level.js';
import { MockPaperSpecSchema } from './mock-paper-spec.js';
import { SourceSlotSchema } from './source-slot.js';
import { COURSE_DRAFT_SCHEMA_VERSION } from './schema-version.js';
import { CurriculumOutlineSchema } from './curriculum-outline.js';
import { ExamTypeSchema } from './exam-type.js';
/** A single question-type slot in the exam. */
const SlotSchema = z.object({
    enabled: z.boolean(),
    /** Expected count in the actual exam (drives mock_exam sampling, NOT bank size). */
    count: z.number().int().nonnegative().optional(),
});
export const ExamFormatSchema = z.object({
    mcq: SlotSchema,
    tf: SlotSchema,
    essay: SlotSchema,
    shortAnswer: SlotSchema,
    durationMinutes: z.number().int().min(15).max(480),
});
/** PDF reference. Bytes stored separately in IndexedDB (this schema only carries the metadata). */
export const CourseDraftSourceSchema = z.object({
    kind: z.literal('pdf'),
    filename: z.string().min(1).max(255),
    /** Lowercase hex sha256 of file bytes; used for de-dup + IndexedDB key. */
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
    /** Byte size, for UI display + sanity check on retrieval. */
    byteLength: z.number().int().nonnegative(),
});
/**
 * PastPaperUpload — same shape as CourseDraftSource (one PDF per upload),
 * plus an `id` for cross-reference from MockPaperSpec.pastPaperRef and an
 * optional `year` for the "default to most recent" UI behavior.
 *
 * Bytes go in IDB pdfs store (key = id). Parsed markdown lands on
 * IngestPackage.pastPapers[] post-ingest.
 */
export const PastPaperUploadSchema = z.object({
    /** Slug. Generated 'past_<yearOrIdx>'. */
    id: z.string().regex(/^[a-z][a-z0-9_-]*$/),
    filename: z.string().min(1).max(255),
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
    byteLength: z.number().int().nonnegative(),
    year: z.number().int().min(1990).max(2100).optional(),
    label: z.string().min(1).max(120).optional(),
});
export const CourseDraftSchema = z.object({
    /**
     * Session 19AL: schema version self-tag. New drafts default to
     * `COURSE_DRAFT_SCHEMA_VERSION`. Old drafts (pre-19AL) without this field
     * load fine due to zod's `.default()`; consumers checking minimum version
     * should use `meetsMinSchemaVersion`. See LLM_NATIVE_ARCHITECTURE §2.4 / §2.5.
     */
    schemaVersion: z.string().optional().default(COURSE_DRAFT_SCHEMA_VERSION),
    /** Slug; lowercase, dash/underscore allowed. Generated from title or anon-uuid. */
    id: z.string().regex(/^[a-z][a-z0-9_-]{0,63}$/),
    title: z.string().min(1).max(120),
    /** Human-readable course code (e.g. "CS3052"). Optional — shown in sidebar top.
     *  Session 3 (19BM-E): added to clear DEBT-19BM-E-SIDEBAR-COURSE-CODE. Old
     *  IDB drafts without this field load fine (zod optional + passthrough). */
    code: z.string().max(32).optional(),
    /** Subject area. 19BO+: 8 broad fields + 'other' escape hatch. */
    discipline: DisciplineSchema,
    /** Optional free-text specifying the exact subject (e.g. "法学", "有机化学").
     *  19BO+: injected into the prompt for ANY discipline, not just 'other' —
     *  this is where granularity lives since the enum collapsed to broad fields. */
    customDiscipline: z.string().min(1).max(80).optional(),
    /** 19BO+ P2-1 AGENTIC-DISCIPLINE-ROUTING: fine-grained exam scenario
     *  (e.g. law→'bar_exam'|'final_postgrad'|'IRAC', medicine→'licensing'|'USMLE').
     *  Drives route_discipline stage to pick the right rule subset instead of
     *  injecting all scenarios at once. Optional — undefined = route falls back
     *  to P1-1 static full-rule injection. Per-discipline options live in
     *  EXAM_TYPES_BY_DISCIPLINE; the wire schema is deliberately loose so new
     *  disciplines' values don't require a schema bump. */
    examType: ExamTypeSchema.optional(),
    /** How the course is taught/tested. Multi-select 1–4. */
    pedagogyTypes: z.array(PedagogyTypeSchema).min(1).max(4),
    /** Free-text pedagogy when pedagogyTypes includes 'custom'. */
    customPedagogy: z.string().min(1).max(80).optional(),
    /** ISO datetime; null = no specific exam date set yet. */
    examDate: z.string().datetime().nullable(),
    examFormat: ExamFormatSchema,
    /** Components the user wants. Order = sidebar nav order in the generated course. */
    enabledComponents: z.array(ComponentIdSchema).min(1),
    /**
     * Session 19S §C-1: User self-assessed familiarity with the material.
     * Drives ingest tone (lecture_notes depth, flashcard volume, question
     * Bloom distribution). Required; default 'partial' applied at form layer.
     */
    masteryLevel: MasteryLevelSchema,
    /**
     * Session 19AE: which language mode the LLM should generate in.
     * Set at course creation, locked for the course's lifetime (changing it
     * after generation would invalidate IngestPackage). Default 'en_zh_gloss'
     * (calibrated for Chinese students preparing for English exams).
     */
    contentLanguage: ContentLanguageSchema.default('en_zh_gloss'),
    /**
     * Session 19S §C-1: Per-paper mock generation specs (0–3 entries).
     * Each spec maps 1:1 to one IngestPackage.mockExamItems.papers[i].
     * Empty array = no mock papers; mock-exam tab uses daily-bank fallback.
     */
    mockPapers: z.array(MockPaperSpecSchema).max(3),
    /**
     * Session 19AD: unified multi-source uploads. Each slot has a `kind` tag
     * (textbook / lecture_notes / past_paper / syllabus / case_study / reading /
     * custom) so each ingest stage pulls only what it needs. Optional during
     * the draft phase (form gates final submit on length≥1).
     *
     * Replaces the old single `source` + `pastPapers[]` model. Legacy fields
     * remain on the schema as optional read-only — `migrateDraft()` in
     * packages/web/src/storage/idb.ts converts them to `sources[]` on first read.
     */
    sources: z.array(SourceSlotSchema).optional(),
    /** @deprecated Session 19AD: use `sources[]`. Kept for back-compat with old IDB drafts. */
    source: CourseDraftSourceSchema.optional(),
    /** @deprecated Session 19AD: past papers are now slots with `kind: 'past_paper'`. Kept for back-compat. */
    pastPapers: z.array(PastPaperUploadSchema).optional(),
    /** Anonymous user id (`examforge.anonId` localStorage). v1 leaves blank. */
    anonId: z.string().optional(),
    /**
     * Sprint 19BH Day 1 Step 1.6 · v4 anchor-first ingest pillar B.
     * Frozen chapter→topic→concept tree. When present, signals the user has
     * locked their outline (via /ingest/lock-outline or the create-course form
     * outline step); the entire ingest pipeline reads from it for anchor
     * enforcement (Step 1.8 superRefine) and audit (Day 3).
     *
     * Optional: drafts created before 19BH or where the user hasn't yet locked
     * an outline parse fine. extract_cases stage (Day 2 Step 2.5) runs in
     * mode B (LLM generation) when this is absent, mode A (skip · already
     * locked) when present.
     */
    curriculumOutline: CurriculumOutlineSchema.optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
}).passthrough();
/** A draft has everything required for ingest. */
export function isDraftReadyForIngest(draft) {
    const hasSource = (draft.sources && draft.sources.length > 0) || Boolean(draft.source);
    return hasSource && draft.title.length > 0 && draft.enabledComponents.length > 0;
}
//# sourceMappingURL=course-draft.js.map